import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { jsPDF } from 'jspdf';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Download as DownloadIcon,
  FactCheck as AuditIcon,
  LocalHospital as MedicalIcon,
  Paid as PaidIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RecalcIcon,
  Send as SendIcon,
  UploadFile as ExportIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import {
  collection,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
  where,
  doc as firestoreDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { isOperatorRole } from '../../redux/slices/authSlice';
import { recalculateMedicalTransportBillingFacts } from '../../services/medicalTransportBillingService';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import MedicalBillingSetupTab from './MedicalBillingSetupTab';
import MedicalTariffsTab from './MedicalTariffsTab';
import {
  buildDmrzCsv,
  collectDmrzIssues,
  dmrzFileName
} from './dmrzFahrtenimport';
import {
  COPAYMENT_LABELS,
  FARE_SOURCE_COLORS,
  FARE_SOURCE_LABELS,
  KM_SOURCE_LABELS,
  TRACK_QUALITY_COLORS,
  TRACK_QUALITY_FILTER_OPTIONS,
  TRACK_QUALITY_LABELS,
  TRIP_DIRECTION_LABELS,
  csvNumber,
  formatKm,
  formatRatio,
  getBillingFacts,
  getDetourRatio,
  getFareSource,
  getOccupiedKm,
  getTrackVerdict
} from './billingFacts';

const BILLING_STATUS_OPTIONS = [
  { value: 'all', label: 'Alle' },
  { value: 'ready', label: 'Bereit' },
  { value: 'exported', label: 'Exportiert' },
  { value: 'submitted', label: 'Eingereicht' },
  { value: 'paid', label: 'Bezahlt' },
  { value: 'rejected', label: 'Beanstandet' },
  { value: 'not_ready', label: 'Nicht bereit' }
];

const PROOF_STATUS_OPTIONS = [
  { value: 'all', label: 'Alle' },
  { value: 'complete', label: 'Vollständig' },
  { value: 'waived', label: 'Nicht möglich' },
  { value: 'problem', label: 'Problem' },
  { value: 'missing', label: 'Fehlt' }
];

const BILLING_LABELS = {
  not_ready: 'Nicht bereit',
  ready: 'Bereit',
  exported: 'Exportiert',
  submitted: 'Eingereicht',
  paid: 'Bezahlt',
  rejected: 'Beanstandet'
};

const BILLING_COLORS = {
  not_ready: 'default',
  ready: 'success',
  exported: 'info',
  submitted: 'primary',
  paid: 'success',
  rejected: 'error'
};

const PROOF_LABELS = {
  missing: 'Fehlt',
  incomplete: 'Unvollständig',
  complete: 'Vollständig',
  waived: 'Nicht möglich',
  problem: 'Problem',
  rejected: 'Beanstandet'
};

const PROOF_COLORS = {
  missing: 'warning',
  incomplete: 'warning',
  complete: 'success',
  waived: 'info',
  problem: 'error',
  rejected: 'error'
};

const PROOF_TYPE_LABELS = {
  digital_signature: 'Digitale Unterschrift',
  paper_proof: 'Papiernachweis',
  facility_confirmation: 'Einrichtung bestätigt',
  cannot_sign: 'Patient kann nicht unterschreiben',
  no_show: 'Patient nicht angetroffen',
  problem: 'Problem'
};

const toDate = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (value) => {
  const date = toDate(value);
  if (!date) return '-';
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const formatDateForCsv = (value) => {
  const date = toDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat('de-DE').format(date);
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return `${Number(value).toFixed(2)} EUR`;
};

const getBillingAmount = (ride) => (
  ride.fare !== null && ride.fare !== undefined && ride.fare !== ''
    ? ride.fare
    : ride.estimatedEarningEur
);

// Ab dieser Umwegquote (Ist-Besetzt-km / Soll-km) wird die Fahrt markiert.
// Bewusst großzügig: Baustellen und Sperrungen sind normal, 20 % Aufschlag
// gegenüber der Routenberechnung sind noch keine Auffälligkeit.
const DETOUR_WARN_RATIO = 1.2;

const csvEscape = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replaceAll('"', '""')}"`;
};

const valueOrDash = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const shortHash = (hash) => {
  if (!hash) return '';
  return hash.length > 16 ? `${hash.slice(0, 16)}...` : hash;
};

const addPdfLine = (pdf, label, value, x, y, maxWidth = 170) => {
  pdf.setFont('helvetica', 'bold');
  pdf.text(`${label}:`, x, y);
  pdf.setFont('helvetica', 'normal');
  const lines = pdf.splitTextToSize(valueOrDash(value), maxWidth - 45);
  pdf.text(lines, x + 45, y);
  return y + Math.max(lines.length, 1) * 6;
};

const parseSignaturePoints = (points) => {
  if (!Array.isArray(points)) return [];
  return points.map(point => {
    if (point === 'break') return null;
    if (typeof point !== 'string') return null;
    const [x, y] = point.split(',').map(Number);
    if (Number.isNaN(x) || Number.isNaN(y)) return null;
    return { x, y };
  });
};

const signatureSegments = (points) => {
  const parsed = parseSignaturePoints(points);
  const segments = [];
  let currentSegment = [];

  parsed.forEach(point => {
    if (!point) {
      if (currentSegment.length > 1) segments.push(currentSegment);
      currentSegment = [];
      return;
    }
    currentSegment.push(point);
  });

  if (currentSegment.length > 1) segments.push(currentSegment);
  return segments;
};

const SignaturePreview = ({ title, points, capturedAt, capturedBy }) => {
  const segments = signatureSegments(points);
  const actualPoints = segments.flat();

  let viewBox = '0 0 600 220';
  if (actualPoints.length > 0) {
    const minX = Math.min(...actualPoints.map(point => point.x));
    const minY = Math.min(...actualPoints.map(point => point.y));
    const maxX = Math.max(...actualPoints.map(point => point.x));
    const maxY = Math.max(...actualPoints.map(point => point.y));
    viewBox = `${minX - 12} ${minY - 12} ${Math.max(maxX - minX + 24, 1)} ${Math.max(maxY - minY + 24, 1)}`;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Stack spacing={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fff',
            height: 180,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {actualPoints.length > 0 ? (
            <svg width="100%" height="100%" viewBox={viewBox} preserveAspectRatio="xMidYMid meet">
              {segments.map((segment, index) => (
                <polyline
                  key={index}
                  points={segment.map(point => `${point.x},${point.y}`).join(' ')}
                  fill="none"
                  stroke="#111"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ))}
            </svg>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Keine Signatur gespeichert
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Erfasst: {formatDateTime(capturedAt)}
          {capturedBy ? ` · ${capturedBy}` : ''}
        </Typography>
      </Stack>
    </Paper>
  );
};

const ProofAuditDialog = ({ ride, open, onClose }) => {
  if (!ride) return null;

  const snapshot = ride.medicalTransportSnapshot || {};
  const audit = ride.proofAudit || {};
  const proofStatus = ride.medicalTransportProofStatus || 'missing';
  const billingStatus = ride.medicalTransportBillingStatus || 'not_ready';
  const proofType = ride.medicalTransportProofType || '';
  const proofNotes = ride.medicalTransportProofNotes || ride.billingNotes || '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Krankenfahrt-Nachweis prüfen</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Fahrt / Patient
                </Typography>
                <Typography variant="body2">{snapshot.patientName || ride.customerName || 'Patient nicht gesetzt'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {snapshot.healthInsuranceName || 'Krankenkasse nicht gesetzt'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDateTime(ride.scheduledTime || ride.completedTimestamp || ride.timestamp)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {ride.start || '-'} → {ride.destination || '-'}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Status
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={PROOF_LABELS[proofStatus] || proofStatus} color={PROOF_COLORS[proofStatus] || 'default'} />
                  <Chip size="small" label={PROOF_TYPE_LABELS[proofType] || proofType || 'Nachweisart fehlt'} variant="outlined" />
                  <Chip size="small" label={BILLING_LABELS[billingStatus] || billingStatus} color={BILLING_COLORS[billingStatus] || 'default'} variant="outlined" />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Erfasst: {formatDateTime(ride.medicalTransportProofCapturedAt)}
                </Typography>
                {proofNotes && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {proofNotes}
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <SignaturePreview
                title="Patientenunterschrift"
                points={ride.medicalTransportSignaturePoints}
                capturedAt={ride.medicalTransportSignatureCapturedAt}
                capturedBy={ride.medicalTransportSignatureCapturedBy}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SignaturePreview
                title="Beförderer-Unterschrift"
                points={ride.medicalTransportDriverSignaturePoints}
                capturedAt={ride.medicalTransportDriverSignatureCapturedAt}
                capturedBy={ride.medicalTransportDriverSignatureCapturedBy}
              />
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Audit
              </Typography>
              {audit.signatureSha256 ? (
                <>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {audit.signatureSha256}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Version: {audit.payloadVersion || '-'} · Berechnet: {formatDateTime(audit.computedAt)}
                  </Typography>
                </>
              ) : (
                <Alert severity="warning">
                  Für diese Fahrt liegt noch kein Proof-Audit-Hash vor. Der Hash wird serverseitig erzeugt, sobald die neue Function deployed ist und Proof-Felder geschrieben werden.
                </Alert>
              )}
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        {ride.medicalTransportOrderId && (
          <Button component={Link} to={`/krankenfahrten/${ride.medicalTransportOrderId}`} onClick={onClose}>
            Verordnung öffnen
          </Button>
        )}
        <Button component={Link} to={`/fahrten/${ride.id}`} onClick={onClose}>
          Fahrt öffnen
        </Button>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

const drawSignature = (pdf, points, x, y, width, height, label = 'Digitale Unterschrift') => {
  pdf.setDrawColor(180, 180, 180);
  pdf.rect(x, y, width, height);
  pdf.setFontSize(8);
  pdf.setTextColor(110, 110, 110);
  pdf.text(label, x + 2, y + 5);
  pdf.setTextColor(0, 0, 0);

  const parsed = parseSignaturePoints(points);
  const actualPoints = parsed.filter(Boolean);
  if (actualPoints.length === 0) {
    pdf.setFontSize(10);
    pdf.text('Keine digitale Signatur gespeichert', x + 4, y + height / 2);
    return;
  }

  const minX = Math.min(...actualPoints.map(point => point.x));
  const minY = Math.min(...actualPoints.map(point => point.y));
  const maxX = Math.max(...actualPoints.map(point => point.x));
  const maxY = Math.max(...actualPoints.map(point => point.y));
  const sourceWidth = Math.max(maxX - minX, 1);
  const sourceHeight = Math.max(maxY - minY, 1);
  const scale = Math.min((width - 8) / sourceWidth, (height - 12) / sourceHeight);
  const offsetX = x + 4;
  const offsetY = y + 8;

  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.6);
  for (let i = 0; i < parsed.length - 1; i += 1) {
    const current = parsed[i];
    const next = parsed[i + 1];
    if (!current || !next) continue;
    pdf.line(
      offsetX + (current.x - minX) * scale,
      offsetY + (current.y - minY) * scale,
      offsetX + (next.x - minX) * scale,
      offsetY + (next.y - minY) * scale
    );
  }
};

const addRideProofPage = (pdf, ride) => {
  const snapshot = ride.medicalTransportSnapshot || {};
  const proofStatus = ride.medicalTransportProofStatus || 'missing';
  const billingStatus = ride.medicalTransportBillingStatus || 'not_ready';
  const proofType = ride.medicalTransportProofType || '';
  const proofNotes = ride.medicalTransportProofNotes || ride.billingNotes || '';
  let y = 18;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.text('Krankenfahrt-Nachweis', 14, y);
  y += 10;

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Fahrt-ID: ${ride.id}`, 14, y);
  pdf.text(`Erstellt: ${formatDateTime(new Date())}`, 125, y);
  y += 10;

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Fahrt', 14, y);
  y += 7;
  pdf.setFontSize(10);
  y = addPdfLine(pdf, 'Datum', formatDateTime(ride.scheduledTime || ride.completedTimestamp || ride.timestamp), 14, y);
  y = addPdfLine(pdf, 'Start', ride.start, 14, y);
  y = addPdfLine(pdf, 'Ziel', ride.destination, 14, y);
  y = addPdfLine(pdf, 'Fahrer', ride.driverName, 14, y);
  y = addPdfLine(pdf, 'Betrag', formatCurrency(getBillingAmount(ride)), 14, y);
  y += 4;

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Verordnung / Patient', 14, y);
  y += 7;
  pdf.setFontSize(10);
  y = addPdfLine(pdf, 'Patient', snapshot.patientName || ride.customerName, 14, y);
  y = addPdfLine(pdf, 'Geburtsdatum', formatDateForCsv(snapshot.patientBirthDate), 14, y);
  y = addPdfLine(pdf, 'Telefon', snapshot.patientPhone || ride.customerPhone, 14, y);
  y = addPdfLine(pdf, 'Krankenkasse', snapshot.healthInsuranceName, 14, y);
  y = addPdfLine(pdf, 'Versichertennummer', snapshot.insuranceNumber, 14, y);
  y = addPdfLine(pdf, 'Verordnungsnummer', snapshot.prescriptionNumber, 14, y);
  y = addPdfLine(pdf, 'Genehmigungsnummer', snapshot.approvalNumber, 14, y);
  y += 4;

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Nachweis / Abrechnung', 14, y);
  y += 7;
  pdf.setFontSize(10);
  y = addPdfLine(pdf, 'Nachweisstatus', PROOF_LABELS[proofStatus] || proofStatus, 14, y);
  y = addPdfLine(pdf, 'Nachweisart', PROOF_TYPE_LABELS[proofType] || proofType || 'Nicht erfasst', 14, y);
  y = addPdfLine(pdf, 'Erfasst am', formatDateTime(ride.medicalTransportProofCapturedAt), 14, y);
  y = addPdfLine(pdf, 'Abrechnung', BILLING_LABELS[billingStatus] || billingStatus, 14, y);
  y = addPdfLine(pdf, 'Audit-Hash', shortHash(ride.proofAudit?.signatureSha256) || 'Nicht vorhanden', 14, y);
  y = addPdfLine(pdf, 'No-Show', ride.noShow ? 'Ja' : 'Nein', 14, y);
  if (proofNotes) {
    y = addPdfLine(pdf, 'Notiz', proofNotes, 14, y);
  }
  y += 6;

  drawSignature(
    pdf,
    ride.medicalTransportSignaturePoints,
    14,
    y,
    86,
    45,
    'Patientenunterschrift'
  );
  drawSignature(
    pdf,
    ride.medicalTransportDriverSignaturePoints,
    110,
    y,
    86,
    45,
    'Beförderer'
  );
};

const MedicalTransportBillingPage = () => {
  const currentUser = useSelector(state => state.auth.user);
  const [rides, setRides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [billingFilter, setBillingFilter] = useState('ready');
  const [proofFilter, setProofFilter] = useState('all');
  const [qualityFilter, setQualityFilter] = useState('all');
  const [detourOnly, setDetourOnly] = useState(false);
  // Tabs statt einer flachen Seite: Alles zum Thema „Wie kommt Geld für meine
  // Krankenfahrten herein?" liegt damit an einer Stelle
  // (krankenfahrten_tarife_konzept.md §8.2). Der Tab „Tarife" folgt mit der
  // optionalen Tarifpflege.
  const [activeTab, setActiveTab] = useState('rides');
  const [fromDate, setFromDate] = useState('');
  const [toDateValue, setToDateValue] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [recalculatingId, setRecalculatingId] = useState('');
  const [proofDialogRide, setProofDialogRide] = useState(null);
  // Einrichtungs-Daten aus dem Assistenten — der DMRZ-Export braucht daraus
  // IK, Verkehrsform, Stammdaten-Zuordnungen und die Formatvarianten.
  const [billingConfig, setBillingConfig] = useState(null);
  const [dmrzPreview, setDmrzPreview] = useState(null);

  const loadRides = async () => {
    setIsLoading(true);
    setError('');
    try {
      const constraints = [
        where('status', '==', 'completed'),
        orderBy('timestamp', 'desc'),
        limit(500)
      ];

      if (isOperatorRole(currentUser) && currentUser?.operatorId) {
        constraints.unshift(where('visibleToOperatorIds', 'array-contains', currentUser.operatorId));
      }

      const snapshot = await getDocs(query(collection(db, 'rides'), ...constraints));
      const nextRides = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(ride => ride.medicalTransportOrderId || ride.isKrankenfahrt || ride.isRechnungsfahrt);
      const auditEntries = await Promise.all(nextRides.map(async ride => {
        try {
          const auditSnapshot = await getDoc(firestoreDoc(db, 'proofAudit', ride.id));
          return [ride.id, auditSnapshot.exists() ? auditSnapshot.data() : null];
        } catch (auditError) {
          console.warn('Proof-Audit konnte nicht geladen werden:', ride.id, auditError);
          return [ride.id, null];
        }
      }));
      const auditByRideId = Object.fromEntries(auditEntries);
      setRides(nextRides.map(ride => ({
        ...ride,
        proofAudit: auditByRideId[ride.id] || null
      })));
      setSelectedIds([]);
    } catch (loadError) {
      console.error('Fehler beim Laden der Krankenfahrt-Abrechnung:', loadError);
      setError(loadError.message || 'Abrechnungsdaten konnten nicht geladen werden.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, currentUser?.operatorId]);

  useEffect(() => {
    let cancelled = false;
    getDoc(firestoreDoc(db, 'appSettings', 'medicalBilling'))
      .then(snapshot => {
        if (!cancelled) setBillingConfig(snapshot.exists() ? snapshot.data() : {});
      })
      .catch(configError => {
        console.warn('Abrechnungs-Einstellungen konnten nicht geladen werden:', configError);
        if (!cancelled) setBillingConfig({});
      });
    return () => { cancelled = true; };
  }, []);

  const filteredRides = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const from = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
    const until = toDateValue ? new Date(`${toDateValue}T23:59:59`) : null;

    return rides.filter(ride => {
      const snapshot = ride.medicalTransportSnapshot || {};
      const rideDate = toDate(ride.scheduledTime || ride.completedTimestamp || ride.timestamp);
      const billingStatus = ride.medicalTransportBillingStatus || 'not_ready';
      const proofStatus = ride.medicalTransportProofStatus || 'missing';

      if (billingFilter !== 'all' && billingStatus !== billingFilter) return false;
      if (proofFilter !== 'all' && proofStatus !== proofFilter) return false;
      if (qualityFilter !== 'all') {
        // 'missing' = Merkmale noch nicht eingefroren (Fahrt jünger als die
        // Karenzzeit der Cloud Function oder Tracking war ganz aus).
        const verdict = getTrackVerdict(ride) || 'missing';
        if (verdict !== qualityFilter) return false;
      }
      // Kassen vergüten nur die kürzeste/wirtschaftlichste Strecke — auffällige
      // Umwege werden gekürzt und sollten vor dem Export geprüft werden.
      if (detourOnly) {
        const ratio = getDetourRatio(ride);
        if (ratio === null || ratio <= DETOUR_WARN_RATIO) return false;
      }
      if (from && rideDate && rideDate < from) return false;
      if (until && rideDate && rideDate > until) return false;

      if (!term) return true;
      return [
        snapshot.patientName,
        snapshot.healthInsuranceName,
        snapshot.prescriptionNumber,
        snapshot.approvalNumber,
        ride.customerName,
        ride.customerPhone,
        ride.start,
        ride.destination,
        ride.driverName
      ].some(value => (value || '').toString().toLowerCase().includes(term));
    });
  }, [billingFilter, detourOnly, fromDate, proofFilter, qualityFilter, rides, searchTerm, toDateValue]);

  const selectedRides = useMemo(
    () => filteredRides.filter(ride => selectedIds.includes(ride.id)),
    [filteredRides, selectedIds]
  );

  const stats = useMemo(() => {
    const initial = { not_ready: 0, ready: 0, exported: 0, submitted: 0, paid: 0, rejected: 0 };
    return rides.reduce((acc, ride) => {
      const status = ride.medicalTransportBillingStatus || 'not_ready';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, initial);
  }, [rides]);

  const toggleSelectAll = (event) => {
    setSelectedIds(event.target.checked ? filteredRides.map(ride => ride.id) : []);
  };

  const toggleSelectRide = (rideId) => {
    setSelectedIds(prev =>
      prev.includes(rideId)
        ? prev.filter(id => id !== rideId)
        : [...prev, rideId]
    );
  };

  const bulkUpdateBillingStatus = async (nextStatus) => {
    if (selectedRides.length === 0) return;
    setBulkSaving(true);
    try {
      await Promise.all(selectedRides.map(ride => updateDoc(firestoreDoc(db, 'rides', ride.id), {
        medicalTransportBillingStatus: nextStatus,
        medicalTransportBillingUpdatedAt: serverTimestamp(),
        medicalTransportBillingUpdatedBy: currentUser?.uid || null
      })));
      await loadRides();
    } catch (saveError) {
      console.error('Fehler beim Aktualisieren des Abrechnungsstatus:', saveError);
      setError(saveError.message || 'Abrechnungsstatus konnte nicht aktualisiert werden.');
    } finally {
      setBulkSaving(false);
    }
  };

  // Neuberechnung aus dem Fahrertracking (Cloud Function). Nötig, wenn
  // Track-Punkte erst nach dem automatischen Einfrieren eingetroffen sind.
  const recalculateFacts = async (rideId) => {
    setRecalculatingId(rideId);
    setError('');
    try {
      await recalculateMedicalTransportBillingFacts(rideId);
      await loadRides();
    } catch (recalcError) {
      console.error('Fehler bei der Neuberechnung der Abrechnungsmerkmale:', recalcError);
      setError(recalcError.message || 'Abrechnungsmerkmale konnten nicht neu berechnet werden.');
    } finally {
      setRecalculatingId('');
    }
  };

  const exportSelectedCsv = () => {
    const rows = selectedRides.length > 0 ? selectedRides : filteredRides;
    if (rows.length === 0) return;

    // Die Kilometer-/Zeit-/Merkmalsspalten sind das, was ein Abrechnungszentrum
    // (DMRZ/opta data) zur Bewertung nach dem Kassenvertrag braucht — die
    // Beträge rechnet dort die Plattform aus dem hinterlegten Vertrag.
    // „Betrag" bleibt bewusst der erfasste Fahrpreis; die Herkunftsspalte sagt,
    // was er wert ist (Preishierarchie §8.1).
    const header = [
      'Fahrt-ID',
      'Datum',
      'Patient',
      'Geburtsdatum',
      'Telefon',
      'Krankenkasse',
      'Kostenträgerkennung (IK)',
      'Versichertennummer',
      'Betriebsstätten-Nr.',
      'Arztnummer',
      'Verordnungsnummer',
      'Genehmigungsnummer',
      'Start',
      'Ziel',
      'Fahrer',
      'Nachweisstatus',
      'Nachweisart',
      'Abrechnung',
      'Besetzt-km',
      'km-Quelle',
      'Anfahrt-km',
      'Soll-km',
      'Umwegquote',
      'Standzeit (min)',
      'Wartezeit Hin/Rück (min)',
      'Gegenfahrt-ID',
      'Fahrtrichtung',
      'Merkmale',
      'Begleitperson',
      'Treppenhilfe (Etagen)',
      'Weitere Fahrgäste',
      'Zuzahlung',
      'Zuzahlung-Quelle',
      'Zuzahlung (EUR)',
      'Track-Qualität',
      'Betrag',
      'Betrag-Herkunft',
      // Guardrail §8.7: Kalkulierte Betraege duerfen NICHT in der Spalte
      // "Betrag" landen — sie sind kein Kassenbetrag.
      'Kalkulation (unverbindlich)',
      'Angewandter Vertrag',
      'Notiz'
    ];

    const lines = rows.map(ride => {
      const snapshot = ride.medicalTransportSnapshot || {};
      const facts = getBillingFacts(ride) || {};
      const verdict = facts.trackQuality?.verdict || '';
      return [
        ride.id,
        formatDateForCsv(ride.scheduledTime || ride.completedTimestamp || ride.timestamp),
        snapshot.patientName || ride.customerName || '',
        formatDateForCsv(snapshot.patientBirthDate),
        snapshot.patientPhone || ride.customerPhone || '',
        snapshot.healthInsuranceName || '',
        snapshot.healthInsuranceIk || '',
        snapshot.insuranceNumber || '',
        snapshot.practiceNumber || '',
        snapshot.physicianNumber || '',
        snapshot.prescriptionNumber || '',
        snapshot.approvalNumber || '',
        ride.start || '',
        ride.destination || '',
        ride.driverName || '',
        PROOF_LABELS[ride.medicalTransportProofStatus || 'missing'] || ride.medicalTransportProofStatus || '',
        PROOF_TYPE_LABELS[ride.medicalTransportProofType] || ride.medicalTransportProofType || '',
        BILLING_LABELS[ride.medicalTransportBillingStatus || 'not_ready'] || ride.medicalTransportBillingStatus || '',
        csvNumber(facts.occupiedKm),
        KM_SOURCE_LABELS[facts.kmSource] || facts.kmSource || '',
        csvNumber(facts.approachKm),
        csvNumber(facts.plannedKm),
        csvNumber(facts.detourRatio, 3),
        facts.standbyMinutes ?? '',
        facts.interTripWaitMinutes ?? '',
        facts.linkedRideId || '',
        TRIP_DIRECTION_LABELS[facts.tripDirection] || '',
        Array.isArray(facts.capabilities) ? facts.capabilities.join(' / ') : '',
        facts.escortPresent === true ? 'ja' : (facts.escortPresent === false ? 'nein' : ''),
        // Leer statt 0, wenn der Fahrer nichts gemeldet hat — „nicht gemeldet"
        // und „keine Treppenhilfe" sind für die Abrechnung nicht dasselbe.
        facts.stairAssist?.flights ?? '',
        facts.sharedRide?.additionalPassengers ?? '',
        COPAYMENT_LABELS[facts.copayment?.status] || facts.copayment?.status || '',
        facts.copayment?.source === 'driver_confirmed' ? 'Fahrer bestätigt' : 'Verordnung',
        csvNumber(facts.copayment?.amountEur),
        TRACK_QUALITY_LABELS[verdict] || verdict,
        getBillingAmount(ride) ?? '',
        FARE_SOURCE_LABELS[getFareSource(ride)] || '',
        csvNumber(facts.fareEstimateEur),
        facts.appliedTarifId || '',
        ride.medicalTransportProofNotes || ride.billingNotes || ''
      ].map(csvEscape).join(';');
    });

    const csv = `\uFEFF${header.map(csvEscape).join(';')}\n${lines.join('\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `krankenfahrten-abrechnung-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * DMRZ-Fahrtenimport. Bewusst zweistufig: Erst zeigen, was DMRZ an der Datei
   * beanstanden würde, dann herunterladen. Ein abgewiesener Import kostet den
   * Unternehmer sonst eine komplette Runde — hochladen, Fehlerliste lesen, bei
   * uns korrigieren, neu exportieren.
   */
  const openDmrzPreview = () => {
    const rows = selectedRides.length > 0 ? selectedRides : filteredRides;
    if (rows.length === 0) return;
    setDmrzPreview({
      rows,
      issues: collectDmrzIssues(rows, billingConfig || {})
    });
  };

  const downloadDmrzCsv = () => {
    if (!dmrzPreview) return;
    const config = billingConfig || {};
    const { bytes, mimeType } = buildDmrzCsv(dmrzPreview.rows, config, config.dmrzFormat || {});
    const blob = new Blob([bytes], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = dmrzFileName();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setDmrzPreview(null);
  };

  const exportProofPdf = (rows, filenamePrefix = 'krankenfahrt-nachweis') => {
    if (!rows || rows.length === 0) return;
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    rows.forEach((ride, index) => {
      if (index > 0) pdf.addPage();
      addRideProofPage(pdf, ride);
    });
    pdf.save(`${filenamePrefix}-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const selectedAll = filteredRides.length > 0 && selectedIds.length === filteredRides.length;
  const selectedSome = selectedIds.length > 0 && selectedIds.length < filteredRides.length;

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Krankenfahrten-Abrechnung"
        description="Abrechnungsbereite Krankenfahrten prüfen, gesammelt markieren und als CSV vorbereiten"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrten', path: '/fahrten' },
          { label: 'Krankenfahrten-Abrechnung', path: '/krankenfahrten/abrechnung' }
        ]}
        secondaryButtons={[
          {
            label: 'Verordnungen',
            icon: <MedicalIcon />,
            path: '/krankenfahrten'
          }
        ]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(event, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="rides" label="Fahrten" />
          <Tab value="setup" label="Abrechnungszentrum" />
          <Tab value="tariffs" label="Tarife" />
        </Tabs>
      </Paper>

      {activeTab === 'setup' && (
        <MedicalBillingSetupTab rides={rides} ridesLoading={isLoading} />
      )}

      {activeTab === 'tariffs' && <MedicalTariffsTab />}

      {activeTab === 'rides' && (
      <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {Object.entries(stats).map(([status, count]) => (
          <Grid key={status} size={{ xs: 6, md: 2 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {BILLING_LABELS[status] || status}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {count}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              label="Suchen"
              placeholder="Patient, Kasse, Route, Fahrer..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="billing-filter-label">Abrechnung</InputLabel>
              <Select
                labelId="billing-filter-label"
                label="Abrechnung"
                value={billingFilter}
                onChange={(event) => setBillingFilter(event.target.value)}
              >
                {BILLING_STATUS_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="proof-filter-label">Nachweis</InputLabel>
              <Select
                labelId="proof-filter-label"
                label="Nachweis"
                value={proofFilter}
                onChange={(event) => setProofFilter(event.target.value)}
              >
                {PROOF_STATUS_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="quality-filter-label">Track-Qualität</InputLabel>
              <Select
                labelId="quality-filter-label"
                label="Track-Qualität"
                value={qualityFilter}
                onChange={(event) => setQualityFilter(event.target.value)}
              >
                {TRACK_QUALITY_FILTER_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, md: 1.5 }}>
            <TextField
              label="Von"
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              fullWidth
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 1.5 }}>
            <TextField
              label="Bis"
              type="date"
              value={toDateValue}
              onChange={(event) => setToDateValue(event.target.value)}
              fullWidth
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Stack spacing={0.5}>
              <Chip
                size="small"
                clickable
                color={detourOnly ? 'warning' : 'default'}
                variant={detourOnly ? 'filled' : 'outlined'}
                label={`Nur Umwege > ${Math.round((DETOUR_WARN_RATIO - 1) * 100)} %`}
                onClick={() => setDetourOnly(value => !value)}
              />
              <Typography variant="body2" color="text.secondary">
                {filteredRides.length} Fahrten · {selectedIds.length} ausgewählt
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {billingConfig?.provider === 'dmrz' && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              disabled={filteredRides.length === 0}
              onClick={openDmrzPreview}
            >
              DMRZ-Datei erzeugen
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={filteredRides.length === 0}
            onClick={exportSelectedCsv}
          >
            {billingConfig?.provider === 'dmrz' ? 'Übersicht als CSV' : 'CSV exportieren'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            disabled={filteredRides.length === 0}
            onClick={() => exportProofPdf(selectedRides.length > 0 ? selectedRides : filteredRides, 'krankenfahrt-nachweise')}
          >
            Nachweis-PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            disabled={selectedRides.length === 0 || bulkSaving}
            onClick={() => bulkUpdateBillingStatus('exported')}
          >
            Als exportiert markieren
          </Button>
          <Button
            variant="outlined"
            startIcon={<SendIcon />}
            disabled={selectedRides.length === 0 || bulkSaving}
            onClick={() => bulkUpdateBillingStatus('submitted')}
          >
            Als eingereicht markieren
          </Button>
          <Button
            variant="outlined"
            startIcon={<PaidIcon />}
            disabled={selectedRides.length === 0 || bulkSaving}
            onClick={() => bulkUpdateBillingStatus('paid')}
          >
            Als bezahlt markieren
          </Button>
        </Stack>
        <Alert severity="info" sx={{ mt: 2 }}>
          Krankenkassen rechnen nach ihren Vergütungsvereinbarungen (§ 133 SGB V)
          ab, nicht nach Ihrem Taxitarif. Verbindlich bewertet wird die Fahrt in
          Ihrem Abrechnungszentrum anhand des dort hinterlegten Kassenvertrags —
          der Chip an jedem Betrag zeigt, woher er stammt. Die Besetzt-Kilometer
          ermittelt NextCab aus dem Fahrertracking; Fahrten mit dem Hinweis
          „Prüfen" oder „Nicht verwertbar" sollten Sie vor dem Export ansehen.
        </Alert>
      </Paper>

      {isLoading ? (
        <Loading message="Krankenfahrt-Abrechnung wird geladen..." />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedAll}
                    indeterminate={selectedSome}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell>Datum</TableCell>
                <TableCell>Patient / Kasse</TableCell>
                <TableCell>Route</TableCell>
                <TableCell>Nachweis</TableCell>
                <TableCell>Abrechnung</TableCell>
                <TableCell>Fahrer</TableCell>
                <TableCell align="right">Besetzt-km</TableCell>
                <TableCell align="right">Betrag</TableCell>
                <TableCell align="right">Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRides.map(ride => {
                const snapshot = ride.medicalTransportSnapshot || {};
                const proofStatus = ride.medicalTransportProofStatus || 'missing';
                const billingStatus = ride.medicalTransportBillingStatus || 'not_ready';
                const hasSignature = Array.isArray(ride.medicalTransportSignaturePoints) && ride.medicalTransportSignaturePoints.length > 0;
                const hasDriverSignature = Array.isArray(ride.medicalTransportDriverSignaturePoints) && ride.medicalTransportDriverSignaturePoints.length > 0;
                const hasAuditHash = !!ride.proofAudit?.signatureSha256;
                const trackVerdict = getTrackVerdict(ride);
                const detourRatio = getDetourRatio(ride);
                const fareSource = getFareSource(ride);

                return (
                  <TableRow key={ride.id} hover selected={selectedIds.includes(ride.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(ride.id)}
                        onChange={() => toggleSelectRide(ride.id)}
                      />
                    </TableCell>
                    <TableCell>{formatDateTime(ride.scheduledTime || ride.completedTimestamp || ride.timestamp)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {snapshot.patientName || ride.customerName || 'Nicht gesetzt'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {snapshot.healthInsuranceName || 'Krankenkasse nicht gesetzt'}
                      </Typography>
                      {snapshot.prescriptionNumber && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          VO: {snapshot.prescriptionNumber}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{ride.start || 'Start unbekannt'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ride.destination || 'Ziel unbekannt'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          label={PROOF_LABELS[proofStatus] || proofStatus}
                          color={PROOF_COLORS[proofStatus] || 'default'}
                          variant="outlined"
                        />
                        {ride.medicalTransportProofType && (
                          <Chip
                            size="small"
                            label={PROOF_TYPE_LABELS[ride.medicalTransportProofType] || ride.medicalTransportProofType}
                            color={ride.medicalTransportProofType === 'no_show' || ride.medicalTransportProofType === 'problem' ? 'error' : 'info'}
                            variant="outlined"
                          />
                        )}
                        {hasSignature && (
                          <Chip size="small" label="Patient" color="success" variant="outlined" />
                        )}
                        {hasDriverSignature && (
                          <Chip size="small" label="Beförderer" color="success" variant="outlined" />
                        )}
                        {hasAuditHash && (
                          <Chip size="small" label={`Audit ${shortHash(ride.proofAudit.signatureSha256)}`} color="success" variant="filled" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={BILLING_LABELS[billingStatus] || billingStatus}
                        color={BILLING_COLORS[billingStatus] || 'default'}
                        variant={billingStatus === 'not_ready' ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                    <TableCell>{ride.driverName || 'Nicht gesetzt'}</TableCell>
                    <TableCell align="right">
                      {/* Besetzt-km aus dem Fahrertracking (Phase
                          `passenger_on_board`) — die Kernposition des
                          Kassenvertrags. Der Chip zeigt, ob die Aufzeichnung
                          dafür belastbar genug ist. */}
                      <Stack spacing={0.5} alignItems="flex-end">
                        <Typography variant="body2">
                          {formatKm(getOccupiedKm(ride))}
                        </Typography>
                        {trackVerdict && trackVerdict !== 'ok' && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={TRACK_QUALITY_LABELS[trackVerdict] || trackVerdict}
                            color={TRACK_QUALITY_COLORS[trackVerdict] || 'default'}
                          />
                        )}
                        {detourRatio !== null && detourRatio > DETOUR_WARN_RATIO && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="warning"
                            label={`Umweg ${formatRatio(detourRatio)}`}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack spacing={0.5} alignItems="flex-end">
                        <Typography variant="body2">
                          {formatCurrency(getBillingAmount(ride))}
                        </Typography>
                        {/* Ohne Herkunft ist ein Betrag bei Krankenfahrten
                            nicht interpretierbar: die Kasse zahlt nach Vertrag,
                            nicht nach Taxameter. */}
                        {fareSource && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={FARE_SOURCE_LABELS[fareSource] || fareSource}
                            color={FARE_SOURCE_COLORS[fareSource] || 'default'}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<AuditIcon />}
                          onClick={() => setProofDialogRide(ride)}
                        >
                          Details
                        </Button>
                        {(!trackVerdict || trackVerdict !== 'ok') && (
                          <Button
                            size="small"
                            startIcon={<RecalcIcon />}
                            disabled={recalculatingId === ride.id}
                            onClick={() => recalculateFacts(ride.id)}
                          >
                            {recalculatingId === ride.id ? 'Rechnet...' : 'Neu berechnen'}
                          </Button>
                        )}
                        <Button
                          size="small"
                          startIcon={<PdfIcon />}
                          onClick={() => exportProofPdf([ride], `krankenfahrt-nachweis-${ride.id.substring(0, 8)}`)}
                        >
                          PDF
                        </Button>
                        <Button
                          component={Link}
                          to={`/fahrten/${ride.id}`}
                          size="small"
                          startIcon={<ViewIcon />}
                        >
                          Öffnen
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredRides.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Alert severity="info">
                      Keine Krankenfahrten für die aktuellen Filter gefunden.
                    </Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      </>
      )}

      <ProofAuditDialog
        ride={proofDialogRide}
        open={!!proofDialogRide}
        onClose={() => setProofDialogRide(null)}
      />

      <Dialog open={!!dmrzPreview} onClose={() => setDmrzPreview(null)} maxWidth="sm" fullWidth>
        <DialogTitle>DMRZ-Fahrtenimport erzeugen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {dmrzPreview?.rows.length} Fahrt{dmrzPreview?.rows.length === 1 ? '' : 'en'} werden
            in das DMRZ-Format (Fahrtenimport v1.1) geschrieben.
          </Typography>

          {dmrzPreview?.issues.length === 0 ? (
            <Alert severity="success">
              Keine Auffälligkeiten. Alle Pflichtangaben sind vorhanden.
            </Alert>
          ) : (
            <>
              {/* Bewusst nur Hinweise, kein Blocker: Wer eine Teilmenge
                  einreichen will, um den Rest später nachzuliefern, soll das
                  können — er muss nur wissen, was fehlt. */}
              <Alert severity="warning" sx={{ mb: 2 }}>
                DMRZ wird diese Punkte vermutlich beanstanden. Sie können die
                Datei trotzdem erzeugen.
              </Alert>
              <Stack spacing={1}>
                {dmrzPreview?.issues.map(issue => (
                  <Paper key={issue.key} variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {issue.label}
                      {!issue.global && ` — ${issue.rideIds.length} Fahrt${issue.rideIds.length === 1 ? '' : 'en'}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {issue.hint}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            Beträge enthält die Datei nicht — DMRZ bewertet jeden Fall aus Ihrem
            Kassenvertrag. Nach dem Hochladen markieren Sie die Fahrten hier als
            „exportiert".
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDmrzPreview(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={downloadDmrzCsv}>
            Datei herunterladen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicalTransportBillingPage;
